// Compile dispatcher that chooses main-thread or worker execution per source.
// It preserves the current compile contract while adding lazy worker offload.
import type { BuildSourceEntry } from '../loader/process/types'
import type { LuisCompileContext } from '../loader/context'
import { getRuntimeFeature } from '../features'
import { compileOnMainThread } from './main-compile'
import { resolveCompileExecution } from './strategy'
import { compileOnWorker } from './worker-compile'
import type { LuisDispatchedCompileResult } from './types'

export type { LuisDispatchedCompileResult } from './types'

export class LuisStaleCompileError extends Error {
  constructor() {
    // Mark compile result as superseded by a newer request.
    super('Superseded by a newer compile request.')
  }
}

// Compile one source using main thread or worker based on current strategy.
export async function dispatchCompileWithContext(
  entry: BuildSourceEntry,
  context: LuisCompileContext,
  debugEnabled: boolean,
  options: { worker: 'auto' | 'main' | 'worker' },
): Promise<LuisDispatchedCompileResult> {
  const execution = resolveCompileExecution(entry, options)
  // Keep main-thread compile when strategy does not require worker.
  if (execution === 'main') {
    // Return main-thread compile result directly.
    return compileOnMainThread(entry, context, debugEnabled)
  } else {
    // Continue worker compile dispatch path.
  }

  try {
    const workerCompile = await compileOnWorker(entry, context, debugEnabled)
    // Fall back to main thread when worker feature could not be loaded.
    if (!workerCompile) {
      // Return main-thread fallback when worker feature is unavailable.
      return compileOnMainThread(entry, context, debugEnabled)
    } else {
      // Continue worker result return path.
    }

    // Return normalized worker compile payload.
    return workerCompile
  } catch (error) {
    // Check whether error is a stale compile supersede signal.
    if (isStaleWorkerCompileError(error)) {
      throw new LuisStaleCompileError()
    } else {
      // Continue main-thread fallback for non-stale worker failures.
    }
    // Return main-thread fallback after worker compile failure.
    return compileOnMainThread(entry, context, debugEnabled)
  }
}

// Detect worker compile rejections caused by superseded same-source requests.
function isStaleWorkerCompileError(error: unknown): boolean {
  // Return true when error message matches the stale compile supersede text.
  return error instanceof Error && error.message === 'Superseded by a newer compile request.'
}

// Prune worker-side incremental caches for detached DOM sources.
export async function pruneWorkerCompileCache(activeSourceIds: string[]): Promise<void> {
  const workerFeature = getRuntimeFeature('worker')
  // Skip prune call when worker host bundle is not loaded.
  if (!workerFeature) {
    return
  } else {
    // Continue worker-side cache pruning.
  }

  await workerFeature.pruneIncrementalCache(activeSourceIds)
}
